// ============================================================================
// SEARCH GLYPHS — transcribed path-for-path from docs/design/handoff/"LifeOS
// Search.dc.html" (10a/10b), the same way every other domain's icon file is.
//
// A LOCAL SET RATHER THAN AN IMPORT, per the precedent in components/school,
// components/interests, components/learning and components/connections. The
// duplication is deliberate: these are 12px glyphs on 20px tiles, drawn at
// stroke 1.9, and a shared file that has to serve an 18px rail icon and a 12px
// result tile ends up serving neither.
//
// COLOUR COMES FROM THE TOKEN, NEVER FROM A PROP: every path is
// stroke="currentColor" and the caller sets `text-<hue>`. A raw hex in here
// would fail the ship gate's brand audit, which is the point.
// ============================================================================

export interface IconProps {
  size?: number
  strokeWidth?: number
  className?: string
}

function Glyph({ size = 12, strokeWidth = 1.9, className, d }: IconProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

/** The field's magnifier, and the one in the no-matches card. */
export const Magnifier = ({ size = 21, strokeWidth = 1.8, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M10.6 4a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2M15.3 15.3 20 20" />
  </svg>
)

/** Clear the field. */
export const Cross = (p: IconProps) => (
  <Glyph strokeWidth={2.2} size={15} {...p} d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
)

/** A lecture transcript, and a voice memo's audio. */
export const Mic = (p: IconProps) => (
  <Glyph
    {...p}
    d="M12 4.6a2.4 2.4 0 0 1 2.4 2.4v4.4a2.4 2.4 0 0 1-4.8 0V7A2.4 2.4 0 0 1 12 4.6M6.6 11.4a5.4 5.4 0 0 0 10.8 0M12 16.8V20"
  />
)

/** A journal entry. */
export const Journal = (p: IconProps) => (
  <Glyph {...p} d="M5 4.6A1.6 1.6 0 0 1 6.6 3H18v18H6.6A1.6 1.6 0 0 1 5 19.4zM5 17.6h13" />
)

/** An announcement, and the "ask in your own words" affordance. */
export const Speech = (p: IconProps) => (
  <Glyph {...p} d="M4 6.4A2.4 2.4 0 0 1 6.4 4h11.2A2.4 2.4 0 0 1 20 6.4v7.2a2.4 2.4 0 0 1-2.4 2.4H9l-5 4z" />
)

/** A document: a syllabus extract, a lecture summary, a saved article. */
export const Document = (p: IconProps) => (
  <Glyph {...p} d="M6 4.6h8.4L18.4 8.6v10.8H6zM9 12h6M9 15.4h4" />
)

/** A task. */
export const CheckSquare = (p: IconProps) => (
  <Glyph
    {...p}
    d="M4 6.6A2.6 2.6 0 0 1 6.6 4h10.8A2.6 2.6 0 0 1 20 6.6v10.8A2.6 2.6 0 0 1 17.4 20H6.6A2.6 2.6 0 0 1 4 17.4zM8.4 12.2l2.6 2.6 4.8-5"
  />
)

/** A Canvas deadline. */
export const Calendar = (p: IconProps) => (
  <Glyph {...p} d="M4 7.4A2.4 2.4 0 0 1 6.4 5h11.2A2.4 2.4 0 0 1 20 7.4v11.2A2.4 2.4 0 0 1 17.6 21H6.4A2.4 2.4 0 0 1 4 18.6zM4 10h16" />
)

/** A learning plan item or session note. */
export const Cap = (p: IconProps) => (
  <Glyph {...p} d="M3 9.6 12 5.2l9 4.4-9 4.4zM7 11.9v4.4c0 1.3 2.2 2.3 5 2.3s5-1 5-2.3v-4.4" />
)

/** A Reddit save. */
export const RedditFace = (p: IconProps) => (
  <Glyph
    {...p}
    d="M12 4.2a7.8 7.8 0 1 1 0 15.6 7.8 7.8 0 0 1 0-15.6M8.6 11.4a1 1 0 1 0 0 .01M15.4 11.4a1 1 0 1 0 0 .01M9 15.2c1.8 1.2 4.2 1.2 6 0"
  />
)

/** A YouTube save. */
export const VideoBox = (p: IconProps) => (
  <Glyph
    {...p}
    d="M3.4 8.4A2.4 2.4 0 0 1 5.8 6h12.4a2.4 2.4 0 0 1 2.4 2.4v7.2a2.4 2.4 0 0 1-2.4 2.4H5.8a2.4 2.4 0 0 1-2.4-2.4zM10.4 9.8 14.6 12l-4.2 2.2z"
  />
)

/**
 * An x.com save. The mockup draws no x.com row, so this glyph has no mockup
 * source — it is drawn in the same idiom and wears the neutral tile the
 * Interests port already chose for this source, because a brand hue this bundle
 * does not carry would be a claim.
 */
export const XMark = (p: IconProps) => <Glyph {...p} d="M6 6l12 12M18 6L6 18" />

/** The RELATED divider's pill. */
export const Sparkle = (p: IconProps) => (
  <Glyph {...p} d="M12 3.4l1.9 4.7 4.7 1.9-4.7 1.9L12 16.6l-1.9-4.7L5.4 10l4.7-1.9z" />
)

/** The affordance arrow. */
export const ArrowRight = (p: IconProps) => (
  <Glyph strokeWidth={2} size={13} {...p} d="M5 12h13M12.6 6l6 6-6 6" />
)

/** `Jump to 12:31` — a circle with a play triangle in it, as two subpaths. */
export const JumpTo = (p: IconProps) => (
  <Glyph
    strokeWidth={2}
    size={11}
    {...p}
    d="M9.6 8.4 17 12l-7.4 3.6zM12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4"
  />
)
